<template>
  <div class="payslips cabinet-page" :class="{ 'is-loading': pageLoading || listLoading }">
    <app-cells :wrap="false">
      <h1 class="title">{{ $t('payslips.title') }}</h1>
      <label class="payslips__year">
        <span class="payslips__year-label">{{ $t('payslips.year') }}</span>
        <v-select
          v-localized-select-label="$t('payslips.year')"
          v-model="selectedYear"
          :options="years"
          :clearable="false"
          :searchable="false"
          class="select select--small"
          @input="onYearChange"
        >
          <template #open-indicator>
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none">
              <path d="M7.872.418 7.61.138a.423.423 0 0 0-.619 0l-2.99 3.164L1.01.135a.422.422 0 0 0-.619 0L.128.413a.483.483 0 0 0 0 .655L3.691 4.85a.437.437 0 0 0 .31.149h.002a.437.437 0 0 0 .31-.149l3.56-3.773A.482.482 0 0 0 8 .747a.477.477 0 0 0-.128-.33Z" fill="#606060"/>
            </svg>
          </template>
          <template #option="year">
            <div class="select__item">{{ year.label }}</div>
          </template>
          <template #selected-option="year">
            <div class="selected">{{ year.label }}</div>
          </template>
        </v-select>
      </label>
    </app-cells>

    <app-page-loader v-if="pageLoading || listLoading" />

    <section v-if="!summaryLoading && summary" class="payslips-summary">
      <h2>{{ $t('payslips.summary', { year: selectedYear }) }}</h2>
      <p>{{ $t('payslips.completed', { count: summary.payslips_count }) }}</p>
      <div class="payslips-summary__grid">
        <div class="payslips-summary__item">
          <span>{{ $t('payslips.grossEarned') }}</span>
          <strong>{{ formatMoney(summary.gross_salary, summary.currency) }}</strong>
        </div>
        <div class="payslips-summary__item">
          <span>{{ $t('payslips.benefits') }}</span>
          <strong>{{ formatMoney(summary.additional_earnings, summary.currency) }}</strong>
        </div>
        <div class="payslips-summary__item">
          <span>{{ $t('payslips.deductions') }}</span>
          <strong>{{ formatMoney(summary.deductions.total, summary.currency) }}</strong>
        </div>
        <div class="payslips-summary__item">
          <span>{{ $t('payslips.accrued') }}</span>
          <strong>{{ formatMoney(summary.accrued_salary, summary.currency) }}</strong>
        </div>
      </div>
    </section>
    <div v-else-if="!summaryLoading && summaryError" class="payslips__summary-error">
      {{ $t('payslips.summaryUnavailable') }}
    </div>

    <cabinet-state
      v-if="!listLoading && listError"
      type="error"
      :title="$t('payslips.unavailable')"
      :message="$t('common.pleaseRetry')"
      @retry="loadPayslips"
    />
    <cabinet-state
      v-else-if="!listLoading && !payslips.length"
      type="empty"
      :title="$t('payslips.none', { year: selectedYear })"
      :message="$t('payslips.appear')"
    />
    <div v-else-if="!listLoading" class="payslips__list">
      <article v-for="payslip in payslips" :key="payslip.id" class="payslip-card">
        <div class="payslip-card__header">
          <div>
            <h2>{{ formatMonth(payslip.month) }}</h2>
            <p>{{ payslip.legal_entity ? payslip.legal_entity.name : $t('common.notSpecified') }}</p>
          </div>
          <strong class="payslip-card__net">{{ formatMoney(payslip.net_salary, payslip.currency) }}</strong>
        </div>

        <div class="payslip-card__totals">
          <div>
            <span>{{ $t('payslips.gross') }}</span>
            <strong>{{ formatMoney(payslip.gross_salary, payslip.currency) }}</strong>
          </div>
          <div>
            <span>{{ $t('payslips.accrued') }}</span>
            <strong>{{ formatMoney(payslip.accrued_salary, payslip.currency) }}</strong>
          </div>
          <div>
            <span>{{ $t('payslips.deductions') }}</span>
            <strong>{{ formatMoney(payslip.deductions.total, payslip.currency) }}</strong>
          </div>
        </div>

        <button
          v-if="detailSections(payslip).length"
          class="payslip-card__details-trigger"
          type="button"
          :aria-expanded="isExpanded(payslip.id) ? 'true' : 'false'"
          @click="toggleDetails(payslip.id)"
        >
          {{ isExpanded(payslip.id) ? $t('payslips.hideDetails') : $t('payslips.showDetails') }}
        </button>

        <div v-if="isExpanded(payslip.id)" class="payslip-card__details">
          <section v-for="section in detailSections(payslip)" :key="section.title">
            <h3>{{ section.title }}</h3>
            <div v-for="row in section.rows" :key="row.label" class="payslip-card__detail-row">
              <span>{{ row.label }}</span>
              <strong>{{ row.negative ? '− ' : '' }}{{ formatMoney(row.value, payslip.currency) }}</strong>
            </div>
          </section>
        </div>

        <app-button
          v-if="payslip.download_available"
          class="btn--stretch payslip-card__download"
          :disabled="isDownloading(payslip.id)"
          @click.native="downloadPdf(payslip)"
        >
          {{ isDownloading(payslip.id) ? $t('payslips.preparingPdf') : $t('payslips.downloadPdf') }}
        </app-button>
      </article>
    </div>

    <app-cells v-if="pages > 1 && !listLoading" class="payslips__pagination" position="center">
      <paginate
        v-model="page"
        :clickHandler="onClickPagination"
        :page-count="pages"
        :prev-text="'<'"
        :next-text="'>'"
        :container-class="'pagination'"
        :page-class="'pagination__item'"
        :page-link-class="'pagination__link'"
        :prev-class="'pagination__item pagination__item--prev'"
        :next-class="'pagination__item pagination__item--next'"
        :next-link-class="'pagination__link pagination__link--next'"
        :prev-link-class="'pagination__link pagination__link--prev'"
      />
    </app-cells>
  </div>
</template>

<script>
import {
  downloadPersonalCabinetPayslip,
  getPersonalCabinetPayslips,
  getPersonalCabinetPayslipsSummary,
} from '@/http'
import CabinetState from './CabinetState'
import { formatLocalizedDate, localeTag } from '@/i18n'

export default {
  name: 'PersonalCabinetPayslips',
  components: { CabinetState },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      selectedYear: currentYear,
      years: Array.from({ length: currentYear - 1999 }, (value, index) => currentYear - index),
      summary: null,
      summaryLoading: false,
      summaryError: false,
      payslips: [],
      listLoading: false,
      pageLoading: false,
      listError: false,
      page: 1,
      pages: 1,
      expandedPayslips: {},
      downloadingPayslips: {},
      summaryRequestId: 0,
      listRequestId: 0,
      yearLoadRequestId: 0,
    }
  },
  created() {
    this.loadYear()
  },
  methods: {
    loadYear() {
      const requestId = ++this.yearLoadRequestId
      this.pageLoading = true
      return Promise.all([this.loadSummary(), this.loadPayslips()])
        .finally(() => {
          if (requestId === this.yearLoadRequestId) this.pageLoading = false
        })
    },
    loadSummary() {
      const requestId = ++this.summaryRequestId
      this.summaryLoading = true
      this.summaryError = false
      return getPersonalCabinetPayslipsSummary({ year: this.selectedYear })
        .then(response => {
          if (requestId === this.summaryRequestId) this.summary = response.data
        })
        .catch(() => {
          if (requestId === this.summaryRequestId) {
            this.summary = null
            this.summaryError = true
          }
        })
        .finally(() => {
          if (requestId === this.summaryRequestId) this.summaryLoading = false
        })
    },
    loadPayslips() {
      const requestId = ++this.listRequestId
      this.listLoading = true
      this.listError = false
      return getPersonalCabinetPayslips({
        year: this.selectedYear,
        page: this.page,
        page_size: 20,
      })
        .then(response => {
          if (requestId !== this.listRequestId) return
          this.payslips = response.data.results
          this.pages = response.data.pages
        })
        .catch(() => {
          if (requestId === this.listRequestId) {
            this.payslips = []
            this.listError = true
          }
        })
        .finally(() => {
          if (requestId === this.listRequestId) this.listLoading = false
        })
    },
    onYearChange() {
      this.page = 1
      this.expandedPayslips = {}
      this.loadYear()
    },
    onClickPagination(page) {
      this.page = page
      this.expandedPayslips = {}
      this.loadPayslips()
    },
    isExpanded(id) {
      return Boolean(this.expandedPayslips[id])
    },
    toggleDetails(id) {
      this.$set(this.expandedPayslips, id, !this.isExpanded(id))
    },
    isDownloading(id) {
      return Boolean(this.downloadingPayslips[id])
    },
    downloadPdf(payslip) {
      if (this.isDownloading(payslip.id)) return

      // Mobile browsers block tabs opened after an asynchronous request.
      // Reserve the tab while the click is still a direct user action, then
      // navigate it to the generated PDF URL when the backend responds.
      const pdfWindow = window.open('', '_blank')
      this.$set(this.downloadingPayslips, payslip.id, true)
      downloadPersonalCabinetPayslip(payslip.id)
        .then(response => {
          const url = response.data && response.data.url
          if (!url) throw new Error('Payslip PDF URL is missing')

          if (pdfWindow) {
            pdfWindow.location.replace(url)
          } else {
            window.location.assign(url)
          }
        })
        .catch(() => {
          if (pdfWindow) pdfWindow.close()
          this.$notify({ type: 'error', title: this.$t('payslips.pdfError') })
        })
        .finally(() => {
          this.$set(this.downloadingPayslips, payslip.id, false)
        })
    },
    detailSections(payslip) {
      const earnings = this.nonZeroRows([
        { label: this.$t('payslips.basicSalary'), value: payslip.earnings.basic_salary },
        { label: this.$t('payslips.allowance'), value: payslip.earnings.allowance },
        { label: this.$t('payslips.benefitsBonuses'), value: payslip.earnings.benefits },
      ])
      const adjustments = this.nonZeroRows([{ label: this.$t('payslips.salaryWarnings'), value: payslip.adjustments.salary_warnings }])
      const deductions = this.nonZeroRows([
        { label: 'NSSF', value: payslip.deductions.nssf_employee, negative: true },
        { label: 'NHIF', value: payslip.deductions.nhif_employee, negative: true },
        { label: 'PAYE', value: payslip.deductions.paye, negative: true },
        { label: 'WHT', value: payslip.deductions.wht, negative: true },
        { label: this.$t('payslips.advance'), value: payslip.deductions.advance, negative: true },
        { label: 'HESLB', value: payslip.deductions.heslb, negative: true },
        { label: this.$t('payslips.staffLoan'), value: payslip.deductions.staff_loan, negative: true },
      ])
      return [
        { title: this.$t('payslips.earnings'), rows: earnings },
        { title: this.$t('payslips.adjustments'), rows: adjustments },
        { title: this.$t('payslips.deductions'), rows: deductions },
      ].filter(section => section.rows.length)
    },
    nonZeroRows(rows) {
      return rows.filter(row => Number(row.value) !== 0)
    },
    formatMoney(value, currency = 'TZS') {
      return `${new Intl.NumberFormat(localeTag(), { maximumFractionDigits: 0 }).format(value || 0)} ${currency}`
    },
    formatMonth(month) {
      return formatLocalizedDate(month, { month: 'long', year: 'numeric' })
    },
  },
}
</script>

<style lang="sass" scoped>
.payslips__year-label
  +visually-hidden
.payslips__year
  display: flex
  align-items: center
  gap: 9px
  color: #707070
  font-size: 15px

.payslips-summary,
.payslip-card
  border: 1px solid #e5e5e5
  border-radius: 14px

.payslips-summary
  min-height: 120px
  margin-bottom: 18px
  padding: 17px

  h2
    margin-bottom: 3px
    color: #303030
    font-size: 18px
    font-weight: 600

  > p
    margin-bottom: 14px
    color: #858585
    font-size: 13px

.payslips-summary__grid
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 9px

.payslips-summary__item
  display: flex
  flex-direction: column
  gap: 5px
  min-width: 0
  padding: 12px
  border-radius: 10px
  background-color: #f4f7f0

  span
    color: #858585
    font-size: 12px

  strong
    color: #437914
    font-size: 15px
    font-weight: 600
    overflow-wrap: anywhere

.payslips__summary-error
  margin-bottom: 18px
  color: #858585
  font-size: 14px

.payslips__list
  display: grid
  gap: 16px

.payslip-card
  padding: 20px

.payslip-card__header
  display: flex
  align-items: flex-start
  justify-content: space-between
  gap: 12px
  margin-bottom: 18px

  h2
    margin-bottom: 4px
    color: #292929
    font-size: 20px
    font-weight: 600

  p
    color: #858585
    font-size: 13px

.payslip-card__net
  flex-shrink: 0
  color: #4f8d12
  font-size: 18px
  font-weight: 600
  text-align: right

.payslip-card__totals
  display: grid
  grid-template-columns: repeat(3, minmax(0, 1fr))
  gap: 7px
  margin-bottom: 15px

  > div
    display: flex
    flex-direction: column
    gap: 5px
    min-width: 0
    padding: 11px 5px
    border-radius: 10px
    background-color: #f6f6f4
    text-align: center

  span
    color: #858585
    font-size: 12px

  strong
    color: #3c3c3c
    font-size: 12px
    font-weight: 600
    overflow-wrap: anywhere

.payslip-card__details-trigger
  margin-bottom: 15px
  color: #4f8d12
  font-size: 14px
  font-weight: 600

.payslip-card__details
  margin-bottom: 18px
  padding: 15px
  border-radius: 10px
  background-color: #f8f8f8

  section + section
    margin-top: 16px
    padding-top: 16px
    border-top: 1px solid #e7e7e7

  h3
    margin-bottom: 9px
    color: #7c7c7c
    font-size: 11px
    font-weight: 600
    letter-spacing: 0.4px
    text-transform: uppercase

.payslip-card__detail-row
  display: flex
  justify-content: space-between
  gap: 18px
  padding: 5px 0
  color: #6d6d6d
  font-size: 14px

  strong
    color: #444
    font-weight: 500
    text-align: right

.payslip-card__download
  margin-top: 6px

.payslips__pagination
  margin-top: 20px
</style>
